import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Zap, Download, Music2, Clapperboard, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** One-tap AI montage: picks music, visual style and titles, then renders the film. */
export default function InstantMontageCard({ eventId, subsCount, exportState, onStarted }) {
  const [starting, setStarting] = useState(false);
  const rendering = exportState.status === "rendering";
  const summary = exportState.summary;

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/events/${eventId}/instant`);
      toast.success("AI is directing your montage");
      onStarted(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Instant montage failed to start");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div data-testid="instant-montage-card" className="mt-8 rounded-3xl bg-[var(--em-night)] text-white p-6 md:p-8 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[var(--em-clay)]/20 blur-2xl" />
      <div className="absolute -bottom-14 left-1/3 w-56 h-56 rounded-full bg-[var(--em-sage)]/15 blur-2xl" />
      <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
        <div className="max-w-xl">
          <p className="font-mono-label text-[var(--em-clay)]">Instant montage</p>
          <h3 className="font-serif-em text-2xl md:text-3xl mt-2">One tap. AI does the rest.</h3>
          <p className="text-white/70 text-sm mt-2">
            AI picks the perfect soundtrack, a cinematic look, and writes your titles — then edits every clip into one film.
          </p>
          {summary && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm" data-testid="instant-summary">
              {summary.track_title && (
                <span className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1"><Music2 size={13} /> {summary.track_title}</span>
              )}
              <span className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1"><Clapperboard size={13} /> {summary.style_label}</span>
            </div>
          )}
          {summary?.reason && <p className="text-white/50 text-xs italic mt-2">"{summary.reason}"</p>}
          {rendering && (
            <p className="text-[var(--em-clay)] text-xs mt-3 flex items-center gap-2" data-testid="instant-stage">
              <Loader2 size={13} className="animate-spin" /> {exportState.stage || "Rendering your film…"}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button
            data-testid="instant-montage-btn"
            onClick={start}
            disabled={starting || rendering || subsCount === 0}
            className="h-12 px-7 rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white text-base btn-lift"
          >
            <Zap className="mr-2 h-5 w-5" />
            {rendering ? "Directing…" : exportState.status === "ready" ? "Remake montage" : "Make my montage"}
          </Button>
          {exportState.status === "ready" && exportState.download_url && (
            <a href={exportState.download_url} target="_blank" rel="noreferrer">
              <Button data-testid="instant-download-btn" variant="outline" className="w-full rounded-full bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
                <Download className="mr-2 h-4 w-4" /> Download film
              </Button>
            </a>
          )}
          {subsCount === 0 && <p className="text-white/50 text-xs text-center">Waiting for the first clip</p>}
        </div>
      </div>
    </div>
  );
}
